import { AuthService } from '../auth/auth.service';

import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';

import { environment } from '../../environments/environment';

import {
  colorMapping,
  colorMappingValues,
  getLegend,
  getMapping,
  hasLegend,
  indexSpan,
  lightOrDark,
  mappingParameterFallback,
} from '../constants/constants';
import {
  colorMappingSnow,
  parameterColor,
  parameterGroupColor,
} from './constants/constants';
import { Observable } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable()
export class EppService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);

  url = environment.url.bffV3;

  greenTableDesign = signal(true);
  colorMappingNames = signal<any>(undefined);
  colorMappings = signal<any>(undefined);
  colorMappingPerParameterset = signal<any>({});

  setGreenTableDesign(value: boolean): void {
    this.greenTableDesign.set(value);
  }

  getGreenTableDesign(): boolean {
    return this.greenTableDesign();
  }

  readColorScheme(): string {
    const storedColor = localStorage.getItem('eopTableColorScheme');
    let color;
    if (storedColor) {
      color = storedColor;
    } else {
      const userProfile = this.auth.getUserProfile();
      if (
        userProfile !== null &&
        userProfile.hasOwnProperty('metadata') &&
        userProfile['metadata'] !== null
      ) {
        const global = userProfile['metadata']['global'];
        if (global && global.hasOwnProperty('eopTableColorScheme')) {
          color = global['eopTableColorScheme'];
        }
      }
    }
    const neutralColor = color || 'green';
    this.setGreenTableDesign(neutralColor === 'green');
    return neutralColor;
  }

  getEppStations(): Array<any> {
    const customerDocument = this.auth.getCustomerDocument();
    const stations = [];
    if (customerDocument.hasOwnProperty('eppProducts')) {
      for (const product of customerDocument.eppProducts) {
        if (this.auth.isProductActive('eppProducts', product.id)) {
          if (product.hasOwnProperty('stations')) {
            for (const station of product.stations) {
              if (
                !this.auth.isUserAllowedToAccessToProduct(
                  product.id,
                  station.stationId
                )
              ) {
                continue;
              }
              stations.push(station);
            }
          }
        }
      }
    }
    return stations;
  }

  getResolutions(): Array<any> {
    const hideDayResolution = [-1, 5]; // don't show 5 days forecast
    const customerDocument = this.auth.getCustomerDocument();
    const resolutions = [];
    if (customerDocument.hasOwnProperty('eppProducts')) {
      for (const product of customerDocument.eppProducts) {
        if (this.auth.isProductActive('eppProducts', product.is)) {
          if (product.hasOwnProperty('resolutions')) {
            for (const resolution of product.resolutions) {
              let alreadyExist = false;
              for (const res of resolutions) {
                if (
                  res.days === resolution.days &&
                  res.hourly === resolution.hourly
                ) {
                  alreadyExist = true;
                  break;
                }
              }
              const dontShowResolution =
                hideDayResolution.indexOf(resolution.days) >= 0;
              if (!alreadyExist && !dontShowResolution) {
                resolutions.push(resolution);
              }
            }
          }
        }
      }
    }
    return resolutions;
  }

  getParameterSets(): Array<any> {
    const customerDocument = this.auth.getCustomerDocument();
    const parameterSets = [];
    if (customerDocument.hasOwnProperty('eppProducts')) {
      for (const product of customerDocument.eppProducts) {
        if (this.auth.isProductActive('eppProducts', product.is)) {
          if (product.hasOwnProperty('parameter_sets')) {
            for (const parameterSet of product.parameter_sets) {
              parameterSets.push(parameterSet);
            }
          }
        }
      }
    }
    return parameterSets;
  }

  async getDefaultParametersetDetails(parametersets: string[]): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      // check localstorage
      const fromLocalStorage = localStorage.getItem('parametersets');
      const colorMappingPerParameterset = localStorage.getItem(
        'colorMappingPerParameterset'
      );
      if (fromLocalStorage && colorMappingPerParameterset) {
        this.colorMappingPerParameterset.set({
          ...JSON.parse(colorMappingPerParameterset),
        });
        resolve(JSON.parse(fromLocalStorage));
      } else {
        this.getParametersetDetailsFromBff(parametersets).subscribe(
          data => {
            localStorage.setItem('parametersets', JSON.stringify(data));
            resolve(data);
          },
          err => {
            console.error('getParametersetDetails', err);
            reject(err);
          }
        );
      }
    });
  }

  getParametersetDetailsFromBff(parametersets: Array<string>): Observable<any> {
    const fullUrl = this.url + '/parameter-sets';
    const body = JSON.stringify(parametersets);
    return this.http
      .post<Array<any>>(fullUrl, body, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      })
      .pipe(
        map(data => {
          if (data.length > 0) {
            const parametersetDetails = {};
            for (const set of data) {
              const name = set.name;
              const colorMapping = set.color_mapping;
              this.colorMappingPerParameterset.update(
                colorMappingPerParameterset => {
                  colorMappingPerParameterset[name] = colorMapping;
                  return { ...colorMappingPerParameterset };
                }
              );
              parametersetDetails[name] = this.filterParameters(
                set.parameter_definitions
              );
              // parametersetDetails[name] = set.parameter_definitions.sort(
              //   (a, b) => a.order - b.order
              // );
            }
            localStorage.setItem(
              'colorMappingPerParameterset',
              JSON.stringify(this.colorMappingPerParameterset())
            );
            return parametersetDetails;
          }
        })
      );
  }

  filterParameters(parameters: any[]): any[] {
    const filteredParameters = [];
    for (const parameter of parameters.sort((a, b) => a.order - b.order)) {
      if (parameter.fe_parameter !== 'hidden') {
        filteredParameters.push(parameter);
      }
    }
    return filteredParameters;
  }

  getColorMappings(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.colorMappings()) {
        resolve();
      } else {
        const mappings = localStorage.getItem('eppColorMappings');
        if (mappings) {
          const colorMappings = JSON.parse(mappings);
          this.colorMappings.set(colorMappings);
          resolve();
        } else {
          this.getColorMappingsFromBff().subscribe(
            data => {
              this.colorMappings.set(data);
              localStorage.setItem('eppColorMappings', JSON.stringify(data));
              resolve();
            },
            error => reject(error)
          );
        }
      }
    });
  }

  getColorMappingsFromBff(): Observable<any> {
    const fullUrl = this.url + '/color-mappings';
    const body = JSON.stringify(this.colorMappingNames());
    return this.http
      .post<Array<any>>(fullUrl, body, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      })
      .pipe(
        map(data => {
          const mappings = {};
          for (const dataset of data) {
            const id = dataset['id'];
            const name = dataset['name'];
            mappings[id] = {};
            for (const definition of dataset['colormapping_definitions']) {
              const parameter = definition['fe_parameter'];
              mappings[id][parameter] = definition['properties'];
            }
          }
          return mappings;
        })
      );
  }

  getColorMappingNames(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (this.colorMappingNames()) {
        resolve();
      } else {
        const names = localStorage.getItem('eppColorMappingNames');
        if (names) {
          const colorMappingNames = JSON.parse(names);
          this.colorMappingNames.set(colorMappingNames);
          resolve();
        } else {
          this.getColorMappingNamesFromBff().subscribe(
            data => {
              localStorage.setItem(
                'eppColorMappingNames',
                JSON.stringify(data)
              );
              this.colorMappingNames.set(data);
              resolve();
            },
            error => reject(error)
          );
        }
      }
    });
  }

  getColorMappingNamesFromBff(): Observable<Array<string>> {
    const fullUrl = this.url + '/color-mappings';
    return this.http.get<Array<string>>(fullUrl);
  }

  getEppData(
    parameterset: string,
    stationId: number,
    days: number,
    resolution: number
  ): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      this.getEppFromBff(parameterset, stationId, days, resolution).subscribe(
        data => {
          resolve(data);
        },
        err => reject(err)
      );
    });
  }

  getEppFromBff(
    parameterset: string,
    stationId: number,
    days: number,
    resolution: number
  ): Observable<any> {
    const special = parameterset === 'met-ocean' ? 'metocean/' : '';
    const fullUrl =
      this.url + '/epp/' + special + stationId + '/' + days + '/' + resolution;
    const body = JSON.stringify(parameterset);
    return this.http
      .post(fullUrl, body, {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
        }),
      })
      .pipe(
        map(data => {
          return data;
        })
      );
  }

  public colorMapping(
    parameterset: string,
    parameter: string,
    value: number
  ): any {
    return colorMapping(
      parameterset,
      parameter,
      value,
      this.greenTableDesign(),
      this.colorMappingPerParameterset(),
      this.colorMappings()
    );
  }

  getMapping(parameterset: string, parameter: string): Array<any> {
    return getMapping(
      parameterset,
      parameter,
      this.colorMappingPerParameterset(),
      this.colorMappings()
    );
  }

  colorMappingValues(parameterset: string, parameter: string): Array<number> {
    return colorMappingValues(
      parameterset,
      parameter,
      this.colorMappingPerParameterset(),
      this.colorMappings()
    );
  }

  mappingParameterFallback(parameter: string): string {
    return mappingParameterFallback(parameter);
  }

  public colorMappingCond(value: number): string {
    const mapping = {
      0: this.getGreenTableDesign() ? 'table-green' : '',
      1: 'table-kng',
      2: 'cond-moist',
      3: 'cond-wet',
      4: 'cond-nass',
      5: 'cond-less',
      6: 'cond-less',
      7: 'cond-less',
      8: 'cond-snow-less',
      9: 'cond-reif',
      10: 'cond-blackIce',
      11: 'cond-eis',
      12: 'cond-snow',
      13: 'cond-snow',
      14: 'cond-reif-more',
      15: 'cond-blackIce-more',
      16: 'cond-eis-more',
      17: 'cond-snow-more',
      18: 'cond-snow-more',
    };
    return mapping.hasOwnProperty(value) ? mapping[value] : mapping[0];
  }

  public colorMappingTT(value: number): string {
    let color = this.getGreenTableDesign() ? 'table-green' : '';

    if (value <= -13) {
      color = 'tt-red3';
    } else if (value <= -6) {
      color = 'tt-red2';
    } else if (value <= 0.04) {
      color = 'tt-red1';
    } else if (value <= 1.04) {
      color = 'tt-orange';
    } else if (value <= 3.04) {
      color = 'tt-yellow';
    }
    return color;
  }

  public colorMappingSnow(value: number): string {
    return colorMappingSnow(value, this.greenTableDesign());
  }

  public colorMappingIndex(parameter: string, value: number): string {
    if (parameter !== 'i_gm') {
      if (
        ((!value || value < 1 || value > 4) && parameter !== 'i_uv') ||
        (parameter === 'i_uv' && value < 3)
      ) {
        return this.getGreenTableDesign() ? 'table-green' : '';
      }
    } else if (value === 1) {
      return this.getGreenTableDesign() ? 'table-green' : '';
    }
    return 'index-' + parameter + '-' + value;
  }

  parameterColor(parameter: string): string {
    return parameterColor(parameter);
  }

  parameterGroupColor(parameter: string): string {
    return parameterGroupColor(parameter);
  }

  lightOrDark(color: string): string {
    return lightOrDark(color);
  }

  hasLegend(parameter: string): boolean {
    return hasLegend(parameter);
  }

  getLegend(parameter: string, parameterset: string): Array<any> {
    return getLegend(
      parameter,
      parameterset,
      this.greenTableDesign(),
      this.colorMappingPerParameterset(),
      this.colorMappings()
    );
  }

  indexSpan(parameter: string, value: number): string | number {
    return indexSpan(parameter, value);
  }

  /*TODO: kann raus wenn globale station search implementiert (timo)*/
  searchStation(
    filter: { name: string } = { name: '' },
    page = 1
  ): Observable<any> {
    if (filter.name.length > 3) {
      const fullUrl = this.url + '/station/search/' + filter.name;
      return this.http.get<any>(fullUrl).pipe(
        tap(response => {
          response = response
            .map(station => new StationSearch(station.id, station.name))
            // Not filtering in the server since in-memory-web-api has somewhat restricted api
            .filter(station => station.name.includes(filter.name));
          return response;
        })
      );
    } else {
      return new Observable<any>(null);
    }
  }
}

export class StationSearch {
  constructor(
    public id: number,
    public name: string
  ) {}
}
